<template>
  <div class="tt-plus-minus">
    <span
      class="minus"
      :class="{ disabled: disabledMinus }"
      @click.stop.prevent="minus"
    ></span>
    <input
      ref="input"
      class="input"
      type="text"
      :tabindex="tabindex"
      v-bind="$attrs"
      :placeholder="placeholder"
      :value="value"
      :disabled="disabled"
      @keydown="keydownHandler"
      @input="inputHandler"
      @focus="focusHandler"
      @blur="blurHandler"
      @compositionstart="compositionHandler"
      @compositionupdate="compositionHandler"
      @compositionend="compositionHandler"
    />
    <span
      class="plus"
      :class="{ disabled: disabledPlus }"
      @click.stop.prevent="plus"
    ></span>
  </div>
</template>

<script>
import numeral from 'numeral'
import NumberInput from 'com/NumberInput'
import getFixedPriceWithMinTick from 'utils/getFixedPriceWithMinTick'
import { isNumber } from 'utils/common'

export default {
  name: 'PlusMinusInput',
  components: { NumberInput },
  data() {
    return {
      isOnComposition: false
    }
  },
  props: {
    value: String | Number,
    min: Number,
    max: Number,
    precision: Number,
    step: Number,
    disabled: Boolean,
    placeholder: String,
    isSubmit: Boolean,
    defaultValue: Number,
    tabindex: { type: Number, default: 0 },
    matchTick: { type: Boolean, default: false },
    matchPrecision: { type: Boolean, default: false },
    matchMax: { type: Boolean, default: false }, // 输入数据超出时匹配最大值
    ticks: Array // 根据输入数据修改 tick 大小 { begin: 0, end: 1, minTick: 0.1 }
  },
  computed: {
    disabledPlus() {
      if (this.disabled) return true
      if (typeof this.max === 'number') {
        return parseFloat(this.value) >= this.max
      }
      return false
    },
    disabledMinus() {
      if (this.disabled) return true
      if (typeof this.min === 'number') {
        return parseFloat(this.value) <= this.min
      }
      return false
    },
    minTick() {
      if (!this.ticks || !this.ticks.length) return this.step || 1
      const value = parseFloat(this.value)
      for (let i = 0; i < this.ticks.length; i++) {
        const tick = this.ticks[i]
        if (value >= tick.begin && value < tick.end) {
          return tick.tickSize || tick.minTick
        }
      }
      return 1
    }
  },
  methods: {
    plus() {
      if (this.disabledPlus) return
      if (this.value) {
        const value = numeral(this.value)
          .add(this.minTick)
          .value()
        this.setValue(this.formatMatch(value))
        return
      }
      this.setValue(this.minTick)
    },
    minus() {
      if (this.disabledMinus) return
      if (this.value) {
        const value = numeral(this.value)
          .subtract(this.minTick)
          .value()
        this.setValue(this.formatMatch(value))
        return
      }
      return this.minTick
    },
    setValue(value) {
      this.$emit('input', value)
    },
    keydownHandler(event) {
      const code = event.code || event.key
      if (code === 'ArrowDown') {
        this.minus()
      }
      if (code === 'ArrowUp') {
        this.plus()
      }
      if (code === 'Enter' && this.isSubmit) {
        this.$emit('submit', event)
      }
    },
    inputHandler() {
      if (this.isOnComposition) return
      const el = this.$refs.input
      let value = this.formatNumber(el.value)

      if (typeof this.max === 'number' && Number(value) > this.max) {
        if (this.matchMax) {
          value = this.formatNumber(String(this.max))
        } else {
          value = this.value
        }
      }
      const start = el.selectionStart || 0
      const end = el.selectionEnd || 0
      el.value = value
      el.setSelectionRange(start, end)
      // 输入的值不能转数字则无效
      if (Number.isNaN(Number(value))) return
      this.setValue(value)
    },
    focusHandler(evt) {
      this.$emit('focus', evt)
    },
    blurHandler(evt) {
      // 设置默认值
      if (
        isNumber(this.defaultValue) &&
        [null, undefined, ''].includes(this.value)
      ) {
        this.setValue(this.defaultValue)
      }
      if (this.value) {
        let value = String(this.value)
        // 小数点结尾则修复
        if (value.slice(-1) === '.') {
          value = value.slice(0, -1)
        }
        if (this.matchPrecision && isNumber(this.precision)) {
          value = Number(value).toFixed(this.precision)
        }
        this.setValue(value)
      }
      this.$emit('blur', evt)
    },
    compositionHandler(evt) {
      if (evt.type === 'compositionstart') {
        this.isOnComposition = true
      } else if (evt.type === 'compositionend') {
        this.isOnComposition = false
        this.inputHandler(evt)
      }
    },
    focus() {
      this.$refs.input.focus()
    },
    blur() {
      this.$refs.input.blur()
    },
    select() {
      this.$refs.input.select()
    },
    formatNumber(value) {
      let num = ''
      let sign = ''

      // 第一位可以是符号
      if (value.indexOf('-') === 0) {
        value = value.slice(1)
        sign = '-'
      }
      // 如果最小值为正数，不能写﹣号
      if (this.min >= 0) {
        sign = ''
      }
      // 无小数
      if (this.precision === 0) {
        num = value.replace(/[^\d]/, '').replace(/(^0+)(\d*)/, '$2')
      } else {
        num = value
          .replace(/([^\d.])/g, '')
          .replace(/(^\d+\.\d*)(\.)/g, '$1')
          .replace(/(\d*)(\..*)?/, (match, p1, p2) => {
            // 整数位存在去除无用的0
            if (p1 && p1.length >= 2) {
              p1 = p1.replace(/(^0+)(.)/, '$2')
            }
            // 小数位存在，整数位补0
            if (p2 && p2.length >= 2) p1 = p1 || '0'
            return p1 + this.formatDecimal(p2)
          })
      }
      return sign + num
    },
    formatDecimal(decimal) {
      if (decimal) {
        return '.' + decimal.replace(/[^\d]/g, '').slice(0, this.precision)
      }
      return ''
    },
    formatMatch(value) {
      if (this.matchTick && this.minTick) {
        value = getFixedPriceWithMinTick(value, this.minTick)
      }
      if (this.matchPrecision && isNumber(this.precision)) {
        value = Number(value).toFixed(this.precision)
      }
      return value
    }
  }
}
</script>

<style lang="less">
.tt-plus-minus {
  position: relative;
  display: inline-block;
  .input {
    height: 32px;
    padding: 0 40px;
    border: solid 1px var(--border-color);
    box-sizing: border-box;
    line-height: 30px;
    text-align: center;
    font-size: 12px;
    background-color: transparent;
    &:not(:disabled):focus {
      border-color: var(--active-color);
    }
    &[disabled] {
      opacity: 0.8;
      cursor: default;
    }
  }
  .plus,
  .minus {
    position: absolute;
    display: block;
    width: 30px;
    height: 30px;
    top: 1px;
    background-color: var(--light-grey);
    user-select: none;
    cursor: pointer;
    &.disabled {
      opacity: 0.8;
      cursor: default;
    }
  }
  .minus {
    left: 1px;
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 10px;
      height: 2px;
      top: 14px;
      left: 10px;
      background-color: #838d9e;
    }
  }
  .plus {
    right: 1px;
    &:before {
      content: '';
      display: block;
      position: absolute;
      width: 10px;
      height: 2px;
      top: 14px;
      left: 10px;
      background-color: #838d9e;
    }
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 2px;
      height: 10px;
      top: 10px;
      left: 14px;
      background-color: #838d9e;
    }
  }
}
</style>
